import React from 'react';
import Navbar from '@/components/Navbar';
import Hero from '@/components/Hero';
import Services from '@/components/Services';
import About from '@/components/About';
import Testimonials from '@/components/Testimonials';
import Contact from '@/components/Contact';
import Footer from '@/components/Footer';
import { useIsMobile } from '@/hooks/use-mobile';

const Index = () => {
  const isMobile = useIsMobile();

  React.useEffect(() => {
    // Update title and meta tags
    document.title = "KTjones Management Consulting";
    
    // Add viewport meta tag if it doesn't exist
    if (!document.querySelector('meta[name="viewport"]')) {
      const meta = document.createElement('meta');
      meta.name = 'viewport';
      meta.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    // Add padding-top to account for fixed header with larger logo
    const paddingTop = isMobile ? '110px' : '120px';
    document.body.style.paddingTop = paddingTop;
    
    // Force a layout refresh
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 100);
  }, [isMobile]);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <Hero />
      <Services />
      <div className="py-8 md:py-16 bg-white">
        <div className="container px-4 sm:px-6">
          <div className="py-10 md:py-14 px-5 md:px-12 bg-maroon text-white rounded-xl text-center">
            <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl mb-4 md:mb-5">Why do operations matter?</h2>
            <p className="text-lg md:text-xl lg:text-2xl mb-8 md:mb-10">Because, they affect <span className="font-semibold">everything</span>.</p>
            
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-8">
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 md:w-20 md:h-20 bg-white/10 rounded-full flex items-center justify-center mb-3 md:mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                </div>
                <h3 className="text-base md:text-xl font-medium mb-1 md:mb-2">Customer Satisfaction</h3>
                <p className="text-sm md:text-base text-white/80">Deliver exceptional experiences</p>
              </div>
              
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 md:w-20 md:h-20 bg-white/10 rounded-full flex items-center justify-center mb-3 md:mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M23 21v-2a4 4 0 0 1 0 7.75"></path>
                  </svg>
                </div>
                <h3 className="text-base md:text-xl font-medium mb-1 md:mb-2">Employee Retention</h3>
                <p className="text-sm md:text-base text-white/80">Empower your team</p>
              </div>
              
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 md:w-20 md:h-20 bg-white/10 rounded-full flex items-center justify-center mb-3 md:mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"></path>
                    <path d="M15.5 7.5c.28.3.5.63.5 1"></path>
                  </svg>
                </div>
                <h3 className="text-base md:text-xl font-medium mb-1 md:mb-2">Financial Health</h3>
                <p className="text-sm md:text-base text-white/80">Optimize resources</p>
              </div>
              
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 md:w-20 md:h-20 bg-white/10 rounded-full flex items-center justify-center mb-3 md:mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <circle cx="12" cy="12" r="6"></circle>
                    <circle cx="12" cy="12" r="2"></circle>
                  </svg>
                </div>
                <h3 className="text-base md:text-xl font-medium mb-1 md:mb-2">Long-term Success</h3>
                <p className="text-sm md:text-base text-white/80">Systems that scale</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <About />
      <Testimonials />
      <Contact />
      <Footer />
      {isMobile && <BackToTopButton />}
    </div>
  );
};

// Add a back to top button component for mobile
const BackToTopButton = () => {
  const [isVisible, setIsVisible] = React.useState(false);

  React.useEffect(() => {
    const toggleVisibility = () => {
      if (window.pageYOffset > 500) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <>
      {isVisible && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-24 right-4 z-40 bg-maroon/80 hover:bg-maroon text-white w-10 h-10 rounded-full flex items-center justify-center shadow-lg transition-all"
          aria-label="Back to top"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m18 15-6-6-6 6"/>
          </svg>
        </button>
      )}
    </>
  );
};

export default Index;
